import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone, EnvelopeSimple, UsersThree, Flag, CircleNotch, CheckCircle,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { formatTime, formatDueET } from "@/lib/dates";

const TZ = "America/New_York";

const TYPE_META = {
  call: { icon: Phone, label: "Call" },
  email: { icon: EnvelopeSimple, label: "Email" },
  meeting: { icon: UsersThree, label: "Meeting" },
  "follow-up": { icon: Flag, label: "Follow-up" },
};

function etNow() {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const p = Object.fromEntries(fmt.formatToParts(new Date()).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, minutes: parseInt(p.hour, 10) * 60 + parseInt(p.minute, 10) };
}

// Plain-language urgency from the record's actual due date/time (ET).
// `exact` carries the exact Eastern time, shown directly in the row.
export function urgencyFor(item) {
  const d = (item.due_date || "").slice(0, 10);
  const exactFull = d ? `Due ${formatDueET(item.due_date, item.due_time)}` : null;
  const exactTime = item.due_time ? `${formatTime(item.due_time)} ET` : null;
  if (item.category === "missing_outcome") {
    return { text: "Missing outcome", exact: exactFull, cls: "text-[#DC2626]", dot: "#DC2626" };
  }
  if (!d) return { text: "No due date", exact: null, cls: "text-[#6D6D70]", dot: "#9CA3AF" };
  const now = etNow();
  const diff = Math.round((Date.parse(d) - Date.parse(now.date)) / 86400000);
  if (diff < 0) {
    const n = Math.abs(diff);
    return { text: `Overdue by ${n} day${n === 1 ? "" : "s"}`, exact: exactFull, cls: "text-[#DC2626]", dot: "#DC2626" };
  }
  if (diff === 0) {
    if (item.due_time) {
      const [h, m] = item.due_time.split(":").map(Number);
      const mins = h * 60 + m - now.minutes;
      if (mins <= 0) return { text: "Overdue — was due today", exact: exactTime, cls: "text-[#DC2626]", dot: "#DC2626" };
      if (mins < 60) return { text: `Due in ${mins} minute${mins === 1 ? "" : "s"}`, exact: exactTime, cls: "text-[#D97706]", dot: "#F59E0B" };
      if (mins < 240) {
        const hrs = Math.round(mins / 60);
        return { text: `Due in ${hrs} hour${hrs === 1 ? "" : "s"}`, exact: exactTime, cls: "text-[#D97706]", dot: "#F59E0B" };
      }
      return { text: "Due today", exact: exactTime, cls: "text-[#D97706]", dot: "#F59E0B" };
    }
    return { text: "Due today", exact: null, cls: "text-[#D97706]", dot: "#F59E0B" };
  }
  if (diff === 1) return { text: "Due tomorrow", exact: exactTime, cls: "text-[#002248]", dot: "#65AB15" };
  return { text: `Due in ${diff} days`, exact: exactFull, cls: "text-[#52525B]", dot: "#65AB15" };
}

// --- Complete dialog: took place? outcome? next step? due? notes? ---
// Verified workflow — unchanged: confirm, outcome, next step + due, notes,
// close original, write history, create next action, no duplicate completion.
function CompleteDialog({ item, onClose, onDone }) {
  const [tookPlace, setTookPlace] = useState(null); // true | false
  const [outcome, setOutcome] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [nextTime, setNextTime] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const isMeetingOrCall = ["call", "meeting"].includes(item.action_type);

  const submit = async (e) => {
    e.preventDefault();
    if (tookPlace === null) return toast.error(isMeetingOrCall ? "Confirm whether it took place" : "Confirm whether it was done");
    if (!outcome.trim()) return toast.error("An outcome is required");
    if (nextStep.trim() && !nextDate) return toast.error("Set a due date for the next step");
    setSaving(true);
    try {
      await api.post(`/activities/${item.activity_id}/complete-with-outcome`, {
        took_place: tookPlace,
        outcome: outcome.trim(),
        next_step: nextStep.trim() || null,
        next_step_due_date: nextStep.trim() ? nextDate : null,
        next_step_due_time: nextStep.trim() ? (nextTime || null) : null,
        notes: notes.trim() || null,
      });
      toast.success(nextStep.trim() ? "Completed — next task created" : "Completed");
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-md sm:max-w-md" data-testid="complete-action-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#002248]">Complete Action</DialogTitle>
          <DialogDescription>
            {item.prospect_name} · {TYPE_META[item.action_type]?.label || "Task"} · due {formatDueET(item.due_date, item.due_time)}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">
              {isMeetingOrCall ? "Did the call or meeting take place?" : "Was this action completed as planned?"} *
            </Label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setTookPlace(true)} data-testid="complete-took-place-yes"
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold ${tookPlace === true ? "border-[#65AB15] bg-[#65AB15]/10 text-[#4c840f]" : "border-[#E4E4E7] text-[#52525B] hover:bg-[#F4F4F5]"}`}>
                Yes
              </button>
              <button type="button" onClick={() => setTookPlace(false)} data-testid="complete-took-place-no"
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold ${tookPlace === false ? "border-[#EF4444] bg-[#EF4444]/5 text-[#B91C1C]" : "border-[#E4E4E7] text-[#52525B] hover:bg-[#F4F4F5]"}`}>
                No
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">What was the outcome? *</Label>
            <Textarea required value={outcome} onChange={(e) => setOutcome(e.target.value)} rows={2}
              className="rounded-md" placeholder="e.g. Spoke with the property manager, proposal requested" data-testid="complete-outcome" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">What is the next step? (optional)</Label>
            <Input value={nextStep} onChange={(e) => setNextStep(e.target.value)}
              className="rounded-md" placeholder="e.g. Send proposal, schedule walkthrough" data-testid="complete-next-step" />
          </div>
          {nextStep.trim() && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Next step due *</Label>
                <DatePicker value={nextDate} onChange={setNextDate} testId="complete-next-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Time (ET)</Label>
                <Input type="time" value={nextTime} onChange={(e) => setNextTime(e.target.value)}
                  className="rounded-md" data-testid="complete-next-time" />
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="rounded-md" data-testid="complete-notes" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-md" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-md bg-[#65AB15] font-semibold text-white hover:bg-[#4c840f]" data-testid="complete-submit">
              {saving ? <CircleNotch size={18} className="animate-spin" /> : "Complete action"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// --- Reschedule dialog ---
function RescheduleDialog({ item, onClose, onDone }) {
  const [date, setDate] = useState(item.due_date || "");
  const [time, setTime] = useState(item.due_time || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Select a new due date");
    setSaving(true);
    try {
      await api.patch(`/activities/${item.activity_id}`, {
        follow_up_date: date, follow_up_time: time || null,
      });
      toast.success("Action rescheduled");
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-md sm:max-w-sm" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#002248]">Change Due Date</DialogTitle>
          <DialogDescription>{item.prospect_name} · currently due {formatDueET(item.due_date, item.due_time)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">New date *</Label>
              <DatePicker value={date} onChange={setDate} testId="reschedule-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Time (ET)</Label>
              <Input type="time" value={time || ""} onChange={(e) => setTime(e.target.value)} className="rounded-md" data-testid="reschedule-time" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-md" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-md bg-[#002248] font-semibold text-white hover:bg-[#001731]" data-testid="reschedule-submit">
              {saving ? <CircleNotch size={18} className="animate-spin" /> : "Save new due date"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Seven visible touch segments — position derived from the prospect's real
// activity timeline (the Nth logged interaction is Touch N).
function TouchProgress({ touch }) {
  if (!touch) return <span className="text-xs text-[#9CA3AF]">—</span>;
  const n = Math.min(touch, 7);
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="whitespace-nowrap text-xs font-medium text-[#52525B]">Touch {n} of 7</span>
      <span className="flex gap-[3px]">
        {Array.from({ length: 7 }, (_, i) => (
          <span key={i} className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: i < n ? "#65AB15" : "#E4E4E7" }} />
        ))}
      </span>
    </span>
  );
}

function initials(name = "") {
  return name.split(" ").map((x) => x[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

function ActionRow({ item, showOwner, onComplete, onReschedule }) {
  const navigate = useNavigate();
  const u = urgencyFor(item);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-[#F0F1F3] px-4 py-2.5 text-sm last:border-0 hover:bg-[#FAFBFC]"
      data-testid={`action-item-${item.activity_id}`}>
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: u.dot }} />
      <Link to={`/prospects/${item.prospect_id}`}
        className="min-w-0 max-w-[180px] truncate font-semibold text-[#002248] hover:text-[#4c840f]"
        title={item.subject || item.prospect_name}
        data-testid={`action-prospect-${item.activity_id}`}>
        {item.prospect_name}
      </Link>
      <span className="inline-flex shrink-0 items-center gap-1 text-xs text-[#52525B]">
        {(() => { const M = TYPE_META[item.action_type] || TYPE_META["follow-up"]; const I = M.icon;
          return (<><I size={13} className="text-[#6D6D70]" /> {M.label}</>); })()}
      </span>
      {showOwner && (
        <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-[#52525B]"
          data-testid={`action-owner-${item.activity_id}`}>
          <span className="flex h-5 w-5 items-center justify-center overflow-hidden rounded-full bg-[#002248] text-[8px] font-bold text-white">
            {item.owner_photo
              ? <img src={item.owner_photo} alt="" className="h-full w-full object-cover" />
              : initials(item.owner_name)}
          </span>
          {item.owner_name}
        </span>
      )}
      <TouchProgress touch={item.touch_number} />
      <span className={`text-xs font-semibold ${u.cls}`} title={`Exact due: ${formatDueET(item.due_date, item.due_time)}`}
        data-testid={`action-due-${item.activity_id}`}>
        {u.text}{u.exact ? <span className="font-normal text-[#52525B]"> · {u.exact}</span> : null}
      </span>
      <span className="ml-auto flex shrink-0 gap-1">
        <Button size="sm" variant="outline" className="h-6 rounded-md px-2 text-[11px] font-bold"
          onClick={() => navigate(`/prospects/${item.prospect_id}`)} data-testid={`action-open-${item.activity_id}`}>
          View Prospect
        </Button>
        <Button size="sm" className="h-6 rounded-md bg-[#65AB15] px-2 text-[11px] font-bold text-white hover:bg-[#4c840f]"
          onClick={() => onComplete(item)} data-testid={`action-complete-${item.activity_id}`}>
          Record Outcome
        </Button>
        <Button size="sm" variant="outline" className="h-6 rounded-md border-[#002248]/30 px-2 text-[11px] font-bold text-[#002248]"
          onClick={() => onReschedule(item)} data-testid={`action-reschedule-${item.activity_id}`}>
          Change Due Date
        </Button>
      </span>
    </div>
  );
}

const FILTERS = [
  ["due_today", "Due Today"],
  ["overdue", "Overdue"],
  ["upcoming", "Upcoming"],
  ["missing_outcome", "Missing Outcome"],
];

// Compact Sales Action Center — controlled by the dashboard, live CRM data only.
export function SalesActionCenter({ isAdmin, data, filter, onFilterChange, onChanged }) {
  const [completeItem, setCompleteItem] = useState(null);
  const [rescheduleItem, setRescheduleItem] = useState(null);

  if (!data) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[#E4E4E7] bg-white px-4 py-3 text-sm text-[#6D6D70]" data-testid="action-center-loading">
        <CircleNotch size={16} className="animate-spin text-[#002248]" /> Loading action center…
      </div>
    );
  }

  const c = data.categories;
  const items = c[filter] || [];
  const completedToday = (c.completed_today || []).length;

  return (
    <div className="rounded-lg border border-[#E4E4E7] bg-white shadow-sm" data-testid="sales-action-center">
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 pb-2 pt-4">
        <div>
          <h2 className="text-base font-bold text-[#002248] md:text-lg">Sales Action Center</h2>
          <p className="text-xs text-[#6D6D70]">
            {data.scope === "company" ? "Prioritized prospect actions and next steps — company-wide." : "Your prioritized actions and next steps."}
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">Times in Miami / Eastern</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 px-4 pb-3">
        {FILTERS.map(([key, label]) => (
          <button key={key} type="button" onClick={() => onFilterChange(key)}
            data-testid={`ac-filter-${key}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
              filter === key
                ? "border-[#65AB15] bg-[#65AB15] text-white"
                : "border-[#E4E4E7] bg-white text-[#52525B] hover:bg-[#F4F4F5]"
            }`}>
            {label}
            <span className={`rounded-full px-1.5 text-[10px] font-black ${filter === key ? "bg-white/25" : "bg-[#F4F4F5] text-[#002248]"}`}
              data-testid={`ac-count-${key}`}>
              {(c[key] || []).length}
            </span>
          </button>
        ))}
        {completedToday > 0 && (
          <span className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-[#4c840f]" data-testid="ac-completed-today">
            <CheckCircle size={13} weight="fill" /> {completedToday} completed today
          </span>
        )}
      </div>
      <div className="hidden border-t border-[#F0F1F3] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#9CA3AF] md:block">
        Prospect · Action type{isAdmin ? " · Owner" : ""} · 7-Touch · Urgency · Actions
      </div>
      <div className="border-t border-[#F0F1F3]" data-testid="ac-rows">
        {items.length === 0 ? (
          <p className="flex items-center gap-1.5 px-4 py-3 text-xs text-[#6D6D70]" data-testid="ac-empty">
            <CheckCircle size={13} weight="fill" className="text-[#65AB15]" />
            No {FILTERS.find(([k]) => k === filter)?.[1].toLowerCase()} actions.
          </p>
        ) : (
          items.map((it) => (
            <ActionRow key={it.activity_id} item={it} showOwner={isAdmin}
              onComplete={setCompleteItem} onReschedule={setRescheduleItem} />
          ))
        )}
      </div>
      {completeItem && (
        <CompleteDialog item={completeItem} onClose={() => setCompleteItem(null)} onDone={onChanged} />
      )}
      {rescheduleItem && (
        <RescheduleDialog item={rescheduleItem} onClose={() => setRescheduleItem(null)} onDone={onChanged} />
      )}
    </div>
  );
}
